import React from "react";
import { Award, Flame, Rocket, Star, Trophy, Zap } from "lucide-react";

const ICONES = { flame: Flame, trophy: Trophy, rocket: Rocket, star: Star, zap: Zap, award: Award };

export const ICONE_OPCOES = [
  { value: "flame", label: "Chama" },
  { value: "trophy", label: "Troféu" },
  { value: "rocket", label: "Foguete" },
  { value: "star", label: "Estrela" },
  { value: "zap", label: "Raio" },
  { value: "award", label: "Medalha" },
];

/** Dourado da marca quando a campanha não escolheu cor. */
const COR_PADRAO = "#d9a300";

export function corDaCampanha(campanha) {
  const cor = campanha?.cor;
  return /^#[0-9a-fA-F]{6}$/.test(String(cor || "")) ? cor : COR_PADRAO;
}

function formatarValor(valor, metrica) {
  if (valor === null || valor === undefined) return "—";
  if (metrica === "CONTRATOS") return Number(valor).toLocaleString("pt-BR");
  return Number(valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

function formatarPeriodo(iso) {
  const [, mes, dia] = String(iso || "").split("-");
  return dia && mes ? `${dia}/${mes}` : "";
}

export function CampanhaIcone({ campanha, className = "h-4 w-4" }) {
  const Icone = ICONES[campanha?.icone] || Flame;
  const batida = Number(campanha?.percentual || 0) >= 100;
  return (
    <Icone
      className={`${className} shrink-0 ${batida ? "" : "motion-safe:animate-pulse"}`}
      style={{ color: corDaCampanha(campanha) }}
      fill="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    />
  );
}

/**
 * A meta e o progresso da campanha, para irem DENTRO da linha do banco no ranking.
 *
 * Fica junto da linha de propósito: como bloco separado embaixo, parecia um aviso solto em vez do
 * banco em destaque.
 *
 * A cor vem da campanha (a marca do banco). Sem medição — Join fora do ar — mostra o aviso em vez
 * de 0%: zero e "não sei" são coisas diferentes para quem persegue a meta.
 */
export default function CampanhaMetaBar({ campanha }) {
  if (!campanha) return null;

  const cor = corDaCampanha(campanha);
  const percentual = campanha.percentual === null || campanha.percentual === undefined
    ? null
    : Math.max(0, Number(campanha.percentual));
  const batida = percentual !== null && percentual >= 100;
  const faltam = campanha.realizado === null || campanha.realizado === undefined
    ? null
    : Math.max(0, Number(campanha.meta) - Number(campanha.realizado));

  if (percentual === null) {
    return (
      <p className="mt-1 text-[11px] text-muted-foreground">
        {campanha.rotulo} · progresso indisponível agora: a Join não respondeu à medição.
      </p>
    );
  }

  // "Sua parte": quanto a carteira de quem esta olhando representa do realizado da empresa.
  // So vem para quem tem escopo -- para admin seria sempre 100%.
  const participacao = campanha.participacaoEscopo;
  const temParticipacao = participacao !== null && participacao !== undefined;

  return (
    <div className="mt-1.5">
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full transition-[width] duration-700"
          style={{ width: `${Math.min(100, percentual)}%`, backgroundColor: cor }}
        />
        {temParticipacao ? (
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700"
            style={{
              width: `${Math.min(100, Math.max(0, Number(campanha.percentualEscopo || 0)))}%`,
              backgroundColor: cor,
              filter: "brightness(0.65)",
            }}
            title="Sua parte da meta"
          />
        ) : null}
      </div>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 text-[11px] text-muted-foreground">
        <span className="font-semibold text-foreground">
          {batida
            ? "Meta batida 🎉"
            : `${percentual.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% de ${formatarValor(campanha.meta, campanha.metrica)}`}
          <span className="ml-1 font-normal text-muted-foreground">(meta da empresa)</span>
        </span>
        <span>
          {temParticipacao
            ? `você: ${formatarValor(campanha.realizadoEscopo, campanha.metrica)} (${Number(participacao).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% do total) · `
            : ""}
          {batida ? "" : `faltam ${formatarValor(faltam, campanha.metrica)} · `}
          {campanha.rotulo} · {formatarPeriodo(campanha.dataInicio)} a {formatarPeriodo(campanha.dataFim)}
          {campanha.diasRestantes > 0 ? ` · ${campanha.diasRestantes} dia(s)` : ""}
        </span>
      </div>
    </div>
  );
}
